import { CachedSecurity } from './cached-security';
import Fuse from 'fuse.js';
import { FuseConfig } from '@Op/Shared/src/states/configuration/fuse-search-config';
import { ICollection } from '..';

/**
 * CachedSecurities manages a searchable collection of market instruments.
 * It uses a multi-phase search approach: O(1) map lookups for exact symbols,
 * manual O(N) prefix matching for performance, and Fuse.js for fuzzy fallback.
 */
export class CachedSecurities implements ICollection<CachedSecurity> {
  data: CachedSecurity[] = [];

  private static fuzzySearchInstance: Fuse<CachedSecurity>;
  private static instance: CachedSecurities;

  /**
   * Optimization: Map for O(1) lookup of exact symbol matches.
   * Keys are uppercase symbols, values are arrays of securities (one symbol can exist on multiple exchanges).
   */
  private static symbolMap: Map<string, CachedSecurity[]> = new Map();

  private constructor() {}

  /**
   * Initializes the search model from raw stock data.
   * Builds the lookup map and initializes the Fuse.js instance for fuzzy fallback.
   * @param items Array of security objects from the backend/cache.
   */
  static fromData = (items: CachedSecurity[]) => {
    const model = new CachedSecurities();
    model.data = items ?? [];

    // Clear and rebuild the symbol map for O(1) lookups
    CachedSecurities.symbolMap.clear();
    items.forEach((item) => {
      const sym = item.symbol.toUpperCase();
      if (!CachedSecurities.symbolMap.has(sym)) {
        CachedSecurities.symbolMap.set(sym, []);
      }
      CachedSecurities.symbolMap.get(sym)?.push(item);
    });

    // Initialize Fuse.js for fuzzy company name searches
    CachedSecurities.fuzzySearchInstance = new Fuse(items, {
      ...FuseConfig,
      shouldSort: false,
      keys: [
        { name: 'symbol', weight: 0.9 }, // Tickers are the primary match target
        { name: 'name', weight: 0.1 },   // Company names are the secondary fallback
      ],
    });
    CachedSecurities.instance = model;
    return model;
  };

  /**
   * Performs a prioritized search across symbol and company name fields.
   * @param query The search string typed by the user.
   * @returns A sorted array of the top 20 most relevant security matches.
   */
  static fuzzySearch = (query: string): CachedSecurity[] => {
    if (!query) return [];

    const MAX_RESULTS = 20;
    const normalizedQuery = query.trim().toUpperCase();
    const wordMatchRegex = CachedSecurities.getWordMatchRegex(query);
    const results: CachedSecurity[] = [];
    const added = new Set<string>();

    /**
     * Internal helper to sort, filter, and add items to the result list unique by key.
     * Respects the overall MAX_RESULTS limit.
     */
    const addUnique = (items: CachedSecurity[]) => {
      items.sort((a, b) => CachedSecurities.sortByRegionPriority(a, b, wordMatchRegex)).forEach((item) => {
        const key = `${item.symbol}-${item.exchangeCode}`;
        if (results.length < MAX_RESULTS && !added.has(key)) {
          results.push(item);
          added.add(key);
        }
      });
    };

    const all = CachedSecurities.instance?.data ?? [];

    // Phase 1: Exact Symbol Match - Prioritizes the specific ticker if typed exactly.
    addUnique(CachedSecurities.symbolMap.get(normalizedQuery) ?? []);
    if (results.length >= MAX_RESULTS) return results;

    // Phase 2: Symbol Prefix Match - Symbols starting with the query (e.g., "AAP" -> "AAPL").
    addUnique(all.filter((i) => i.symbol.toUpperCase().startsWith(normalizedQuery) && i.symbol.toUpperCase() !== normalizedQuery));
    if (results.length >= MAX_RESULTS) return results;

    // Phase 3: Name Prefix Match - Company names starting with the query (e.g., "INTEL" -> "INTEL CORP").
    addUnique(all.filter((i) => i.name.toUpperCase().startsWith(normalizedQuery) && !i.symbol.toUpperCase().startsWith(normalizedQuery)));
    if (results.length >= MAX_RESULTS) return results;

    // Phase 4: Fuzzy Fallback - Handles typos and mid-name matches using Fuse.js.
    addUnique(
      CachedSecurities.fuzzySearchInstance
        .search(query)
        .map((r) => r.item)
        .filter((i) => !i.symbol.toUpperCase().startsWith(normalizedQuery) && !i.name.toUpperCase().startsWith(normalizedQuery)),
    );

    return results;
  };

  /**
   * Generates a Regex for word boundary matching while escaping special characters.
   * Pre-compiling this avoids repetitive creation inside sorting loops (Performance).
   */
  private static getWordMatchRegex = (q: string) =>
    new RegExp(`\\b${q.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&').toUpperCase()}\\b`, 'i');

  /**
   * Primary sorting heuristic for all search results.
   * Priority: 
   * 1. Symbol Length (Primary listings vs derivatives/warrants)
   * 2. Symbol Lexical Identity (Groups identical symbols and ensures alphabetical consistency)
   * 3. Region (US > CA tie-breaker)
   * 4. Name Length (Shorter names usually indicate parent/primary co)
   * 5. Word Match (Direct company identity match)
   */
  private static sortByRegionPriority = (a: CachedSecurity, b: CachedSecurity, wordMatchRegex?: RegExp) => {
    // 1. Symbol Length (Prioritizes base symbols over derivatives/warrants)
    if (a.symbol.length !== b.symbol.length) {
      return a.symbol.length - b.symbol.length;
    }

    // 2. Symbol Lexical Identity (Groups identical symbols and ensures alphabetical consistency)
    const symbolCompare = a.symbol.localeCompare(b.symbol);
    if (symbolCompare !== 0) {
      return symbolCompare;
    }

    // 3. Region Priority (US > CA > others) - Final tie-breaker for identical symbols
    const regionPriority = ['US', 'CA'];
    const regionA = (a.region || '').trim().toUpperCase();
    const regionB = (b.region || '').trim().toUpperCase();

    const ra = regionPriority.indexOf(regionA);
    const rb = regionPriority.indexOf(regionB);

    if (ra !== rb) {
      return (ra === -1 ? 99 : ra) - (rb === -1 ? 99 : rb);
    }

    // 4. Name Length (Shorter names usually indicate the primary listing)
    if (a.name.length !== b.name.length) {
      return a.name.length - b.name.length;
    }

    // 5. Word Boundary Check (Name)
    if (wordMatchRegex) {
      const isFullWordA = wordMatchRegex.test(a.name);
      const isFullWordB = wordMatchRegex.test(b.name);

      if (isFullWordA !== isFullWordB) {
        return isFullWordA ? -1 : 1;
      }
    }

    return 0;
  };

  /**
   * Determines the absolute best match for a query (used for Enter key presses).
   * Uses Short-Circuiting: Returns the first valid match from the hierarchy 
   * and stops searching deeper levels for better performance.
   */
  static findBestMatch = (query: string): CachedSecurity | undefined => {
    if (!query) return undefined;
    const norm = query.trim().toUpperCase();
    const rx = CachedSecurities.getWordMatchRegex(query);
    
    // Sort logic is shared with the main search for result consistency
    const pickBest = (items: CachedSecurity[]) => (items.length ? [...items].sort((a, b) => this.sortByRegionPriority(a, b, rx))[0] : undefined);

    const all = CachedSecurities.instance?.data ?? [];
    return (
      pickBest(CachedSecurities.symbolMap.get(norm) ?? []) ||   // 1. Exact Ticker
      pickBest(all.filter((i) => i.name.toUpperCase() === norm)) || // 2. Exact Name
      pickBest(all.filter((i) => i.name.toUpperCase().startsWith(norm))) || // 3. Name Prefix
      CachedSecurities.fuzzySearch(query)[0] // 4. Fuzzy fallback
    );
  };
}
